// Integration Profiles & SMS Broadcast Engine Client Library (PRD §1.8 / WP-11).
// Exact config field schemas for 17 providers, secret masking helpers, GSM-7/Unicode SMS segment calculator, and callables.

import { call } from './callable';

export type IntegrationKind = 'email' | 'sms' | 'autoresponder';

export const EMAIL_PROVIDERS = ['smtp', 'mailgun', 'postmark', 'ses', 'mandrill'] as const;
export type EmailProvider = (typeof EMAIL_PROVIDERS)[number];

export const SMS_PROVIDERS = [
  'twilio',
  'vonage',
  'plivo',
  'clickatell',
  'africastalking',
  'http_get',
  'http_post',
] as const;
export type SmsProvider = (typeof SMS_PROVIDERS)[number];

export const AUTORESPONDER_PROVIDERS = [
  'mailchimp',
  'brevo',
  'activecampaign',
  'mautic',
  'mailwizz',
] as const;
export type AutoresponderProvider = (typeof AUTORESPONDER_PROVIDERS)[number];

export type ProviderName = EmailProvider | SmsProvider | AutoresponderProvider;

export interface IntegrationProfileDoc {
  id: string;
  tenantId: string;
  kind: IntegrationKind;
  provider: ProviderName;
  name: string;
  config: Record<string, any>;
  secretFields: string[];
  verified: boolean;
  lastTestAt: number | null;
  lastError: string | null;
  createdAt: number;
  updatedAt: number;
}

export interface ProviderFieldDef {
  key: string;
  labelKey: string;
  defaultLabel: string;
  type: 'text' | 'number' | 'password' | 'select' | 'boolean' | 'textarea' | 'headers';
  required?: boolean;
  placeholder?: string;
  options?: { value: string; label: string }[];
  helperTextKey?: string;
  defaultHelperText?: string;
}

export interface ProviderMeta {
  provider: ProviderName;
  kind: IntegrationKind;
  name: string;
  icon: string;
  descKey: string;
  defaultDesc: string;
  fields: ProviderFieldDef[];
  secretFields: string[];
}

export const PROVIDER_REGISTRY: Record<ProviderName, ProviderMeta> = {
  // ── Email (5) ──────────────────────────────────────────────────────────────
  smtp: {
    provider: 'smtp',
    kind: 'email',
    name: 'SMTP Server',
    icon: '✉️',
    descKey: 'integrations.smtp.desc',
    defaultDesc: 'Custom SMTP mail server over TLS or STARTTLS.',
    secretFields: ['password'],
    fields: [
      { key: 'host', labelKey: 'integrations.field.host', defaultLabel: 'SMTP Host', type: 'text', required: true, placeholder: 'smtp.example.com' },
      { key: 'port', labelKey: 'integrations.field.port', defaultLabel: 'Port', type: 'number', required: true, placeholder: '465 / 587' },
      { key: 'secure', labelKey: 'integrations.field.secureTls', defaultLabel: 'Use TLS (Port 465)', type: 'boolean' },
      { key: 'username', labelKey: 'integrations.field.username', defaultLabel: 'Username / Login', type: 'text', placeholder: 'user@example.com' },
      { key: 'password', labelKey: 'integrations.field.password', defaultLabel: 'Password', type: 'password', required: true },
      { key: 'fromEmail', labelKey: 'integrations.field.fromEmail', defaultLabel: 'From Email Address', type: 'text', required: true, placeholder: 'notifications@example.com' },
      { key: 'fromName', labelKey: 'integrations.field.fromName', defaultLabel: 'From Name (Optional)', type: 'text', placeholder: 'Company Support' },
    ],
  },
  mailgun: {
    provider: 'mailgun',
    kind: 'email',
    name: 'Mailgun',
    icon: '🔫',
    descKey: 'integrations.mailgun.desc',
    defaultDesc: 'Transactional email API by Sinch Mailgun.',
    secretFields: ['apiKey'],
    fields: [
      { key: 'domain', labelKey: 'integrations.field.domain', defaultLabel: 'Mailgun Sending Domain', type: 'text', required: true, placeholder: 'mg.example.com' },
      { key: 'apiKey', labelKey: 'integrations.field.apiKey', defaultLabel: 'Mailgun Private API Key', type: 'password', required: true, placeholder: 'key-xxxxxxxxxxxx' },
      {
        key: 'region',
        labelKey: 'integrations.field.region',
        defaultLabel: 'API Region',
        type: 'select',
        options: [
          { value: 'us', label: 'US (api.mailgun.net)' },
          { value: 'eu', label: 'EU (api.eu.mailgun.net)' },
        ],
      },
      { key: 'fromEmail', labelKey: 'integrations.field.fromEmail', defaultLabel: 'From Email Address', type: 'text', required: true, placeholder: 'info@example.com' },
      { key: 'fromName', labelKey: 'integrations.field.fromName', defaultLabel: 'From Name (Optional)', type: 'text', placeholder: 'Brand Name' },
    ],
  },
  postmark: {
    provider: 'postmark',
    kind: 'email',
    name: 'Postmark',
    icon: '📫',
    descKey: 'integrations.postmark.desc',
    defaultDesc: 'High-deliverability transactional email service by ActiveCampaign.',
    secretFields: ['serverToken'],
    fields: [
      { key: 'serverToken', labelKey: 'integrations.field.serverToken', defaultLabel: 'Postmark Server API Token', type: 'password', required: true, placeholder: 'xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx' },
      { key: 'fromEmail', labelKey: 'integrations.field.fromEmail', defaultLabel: 'Sender Signature Email', type: 'text', required: true, placeholder: 'support@example.com' },
      { key: 'fromName', labelKey: 'integrations.field.fromName', defaultLabel: 'From Name (Optional)', type: 'text', placeholder: 'Support Team' },
    ],
  },
  ses: {
    provider: 'ses',
    kind: 'email',
    name: 'Amazon SES',
    icon: '☁️',
    descKey: 'integrations.ses.desc',
    defaultDesc: 'Amazon Simple Email Service cloud sending.',
    secretFields: ['secretAccessKey'],
    fields: [
      { key: 'region', labelKey: 'integrations.field.awsRegion', defaultLabel: 'AWS Region', type: 'text', required: true, placeholder: 'us-east-1' },
      { key: 'accessKeyId', labelKey: 'integrations.field.accessKeyId', defaultLabel: 'SMTP Username / Access Key ID', type: 'text', required: true },
      { key: 'secretAccessKey', labelKey: 'integrations.field.secretAccessKey', defaultLabel: 'SMTP Password / Secret Access Key', type: 'password', required: true },
      { key: 'fromEmail', labelKey: 'integrations.field.fromEmail', defaultLabel: 'Verified Sender Email', type: 'text', required: true, placeholder: 'verified@example.com' },
      { key: 'fromName', labelKey: 'integrations.field.fromName', defaultLabel: 'From Name (Optional)', type: 'text', placeholder: 'Customer Service' },
    ],
  },
  mandrill: {
    provider: 'mandrill',
    kind: 'email',
    name: 'Mandrill (Mailchimp)',
    icon: '🐵',
    descKey: 'integrations.mandrill.desc',
    defaultDesc: 'Mailchimp Transactional Email API (Mandrill).',
    secretFields: ['apiKey'],
    fields: [
      { key: 'apiKey', labelKey: 'integrations.field.apiKey', defaultLabel: 'Mandrill API Key', type: 'password', required: true, placeholder: 'md-xxxxxxxxxxxx' },
      { key: 'fromEmail', labelKey: 'integrations.field.fromEmail', defaultLabel: 'Verified From Email', type: 'text', required: true, placeholder: 'orders@example.com' },
      { key: 'fromName', labelKey: 'integrations.field.fromName', defaultLabel: 'From Name (Optional)', type: 'text', placeholder: 'Order Desk' },
    ],
  },

  // ── SMS (7) ────────────────────────────────────────────────────────────────
  twilio: {
    provider: 'twilio',
    kind: 'sms',
    name: 'Twilio SMS',
    icon: '🔴',
    descKey: 'integrations.twilio.desc',
    defaultDesc: 'Global SMS messaging via Twilio Programmable SMS.',
    secretFields: ['authToken'],
    fields: [
      { key: 'accountSid', labelKey: 'integrations.field.accountSid', defaultLabel: 'Account SID', type: 'text', required: true, placeholder: 'ACxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx' },
      { key: 'authToken', labelKey: 'integrations.field.authToken', defaultLabel: 'Auth Token', type: 'password', required: true },
      { key: 'fromNumber', labelKey: 'integrations.field.fromNumber', defaultLabel: 'Sender Phone Number / Sender ID', type: 'text', required: true, placeholder: '+15551234567' },
    ],
  },
  vonage: {
    provider: 'vonage',
    kind: 'sms',
    name: 'Vonage (Nexmo)',
    icon: '🟣',
    descKey: 'integrations.vonage.desc',
    defaultDesc: 'Vonage Communications SMS API.',
    secretFields: ['apiSecret'],
    fields: [
      { key: 'apiKey', labelKey: 'integrations.field.apiKey', defaultLabel: 'API Key', type: 'text', required: true },
      { key: 'apiSecret', labelKey: 'integrations.field.apiSecret', defaultLabel: 'API Secret', type: 'password', required: true },
      { key: 'fromNumber', labelKey: 'integrations.field.fromNumber', defaultLabel: 'Sender Number or Alphanumeric ID', type: 'text', required: true, placeholder: 'BrandName or +1234567890' },
    ],
  },
  plivo: {
    provider: 'plivo',
    kind: 'sms',
    name: 'Plivo SMS',
    icon: '🟢',
    descKey: 'integrations.plivo.desc',
    defaultDesc: 'Plivo Global SMS platform.',
    secretFields: ['authToken'],
    fields: [
      { key: 'authId', labelKey: 'integrations.field.authId', defaultLabel: 'Auth ID', type: 'text', required: true, placeholder: 'MAxxxxxxxxxxxxxxxxxx' },
      { key: 'authToken', labelKey: 'integrations.field.authToken', defaultLabel: 'Auth Token', type: 'password', required: true },
      { key: 'fromNumber', labelKey: 'integrations.field.fromNumber', defaultLabel: 'Source Number / Sender ID', type: 'text', required: true, placeholder: '+15559876543' },
    ],
  },
  clickatell: {
    provider: 'clickatell',
    kind: 'sms',
    name: 'Clickatell',
    icon: '🟡',
    descKey: 'integrations.clickatell.desc',
    defaultDesc: 'Clickatell Connect SMS Gateway.',
    secretFields: ['apiKey'],
    fields: [
      { key: 'apiKey', labelKey: 'integrations.field.apiKey', defaultLabel: 'REST API Key', type: 'password', required: true },
      { key: 'fromNumber', labelKey: 'integrations.field.fromNumber', defaultLabel: 'Sender ID (Optional)', type: 'text', placeholder: 'Leave empty for default' },
    ],
  },
  africastalking: {
    provider: 'africastalking',
    kind: 'sms',
    name: "Africa's Talking",
    icon: '🌍',
    descKey: 'integrations.africastalking.desc',
    defaultDesc: "Pan-African SMS gateway API across Kenya, Nigeria, South Africa, etc.",
    secretFields: ['apiKey'],
    fields: [
      { key: 'username', labelKey: 'integrations.field.username', defaultLabel: 'App Username', type: 'text', required: true, placeholder: 'sandbox or app_username' },
      { key: 'apiKey', labelKey: 'integrations.field.apiKey', defaultLabel: 'API Key', type: 'password', required: true },
      { key: 'fromSenderId', labelKey: 'integrations.field.fromSenderId', defaultLabel: 'Shortcode or Alphanumeric Sender ID (Optional)', type: 'text', placeholder: 'AT_SMS' },
    ],
  },
  http_get: {
    provider: 'http_get',
    kind: 'sms',
    name: 'Custom HTTP GET Gateway',
    icon: '🔗',
    descKey: 'integrations.httpGet.desc',
    defaultDesc: 'Dispatch SMS through any provider via a GET request with query placeholders.',
    secretFields: [],
    fields: [
      {
        key: 'url',
        labelKey: 'integrations.field.urlWithPlaceholders',
        defaultLabel: 'Endpoint URL (with {{to}} and {{body}})',
        type: 'text',
        required: true,
        placeholder: 'https://sms.gateway.com/api?user=abc&phone={{to}}&msg={{body}}',
        helperTextKey: 'integrations.httpGet.helper',
        defaultHelperText: 'Placeholders: {{to}} (E.164 phone) and {{body}} (encoded text)',
      },
    ],
  },
  http_post: {
    provider: 'http_post',
    kind: 'sms',
    name: 'Custom HTTP POST Gateway',
    icon: '📨',
    descKey: 'integrations.httpPost.desc',
    defaultDesc: 'Dispatch SMS through any custom provider endpoint via JSON POST with SSRF guard.',
    secretFields: [],
    fields: [
      { key: 'url', labelKey: 'integrations.field.endpointUrl', defaultLabel: 'Endpoint URL', type: 'text', required: true, placeholder: 'https://sms.gateway.com/v1/send' },
      {
        key: 'bodyTemplate',
        labelKey: 'integrations.field.bodyTemplate',
        defaultLabel: 'JSON Body Template (Optional)',
        type: 'textarea',
        placeholder: '{\n  "recipient": "{{to}}",\n  "message": "{{body}}"\n}',
        helperTextKey: 'integrations.httpPost.helper',
        defaultHelperText: 'Leave empty for default {"to": "{{to}}", "message": "{{body}}"} format.',
      },
    ],
  },

  // ── Autoresponder (5) ──────────────────────────────────────────────────────
  mailchimp: {
    provider: 'mailchimp',
    kind: 'autoresponder',
    name: 'Mailchimp',
    icon: '🐵',
    descKey: 'integrations.mailchimp.desc',
    defaultDesc: 'Sync WhatsApp contacts and leads directly to a Mailchimp Audience.',
    secretFields: ['apiKey'],
    fields: [
      { key: 'apiKey', labelKey: 'integrations.field.apiKey', defaultLabel: 'Mailchimp API Key', type: 'password', required: true, placeholder: 'xxxxxxxxxxxx-us1' },
      { key: 'serverPrefix', labelKey: 'integrations.field.serverPrefix', defaultLabel: 'Datacenter Prefix (Optional)', type: 'text', placeholder: 'e.g. us1, us6 (auto-detected if blank)' },
      { key: 'listId', labelKey: 'integrations.field.listId', defaultLabel: 'Default Audience / List ID', type: 'text', required: true, placeholder: 'xxxxxxxxxx' },
    ],
  },
  brevo: {
    provider: 'brevo',
    kind: 'autoresponder',
    name: 'Brevo (Sendinblue)',
    icon: '📫',
    descKey: 'integrations.brevo.desc',
    defaultDesc: 'Sync contacts to Brevo lists with phone number and custom attributes.',
    secretFields: ['apiKey'],
    fields: [
      { key: 'apiKey', labelKey: 'integrations.field.apiKey', defaultLabel: 'Brevo API Key (v3)', type: 'password', required: true, placeholder: 'xkeysib-xxxxxxxxxxxx' },
      { key: 'listId', labelKey: 'integrations.field.listId', defaultLabel: 'Folder / List ID (Optional)', type: 'number', placeholder: 'e.g. 2' },
    ],
  },
  activecampaign: {
    provider: 'activecampaign',
    kind: 'autoresponder',
    name: 'ActiveCampaign',
    icon: '⚡',
    descKey: 'integrations.activecampaign.desc',
    defaultDesc: 'Sync subscribers and trigger marketing automations in ActiveCampaign.',
    secretFields: ['apiToken'],
    fields: [
      { key: 'apiUrl', labelKey: 'integrations.field.apiUrl', defaultLabel: 'Account API URL', type: 'text', required: true, placeholder: 'https://youraccount.api-us1.com' },
      { key: 'apiToken', labelKey: 'integrations.field.apiToken', defaultLabel: 'API Token', type: 'password', required: true },
      { key: 'listId', labelKey: 'integrations.field.listId', defaultLabel: 'Default List ID (Optional)', type: 'text', placeholder: 'e.g. 1' },
    ],
  },
  mautic: {
    provider: 'mautic',
    kind: 'autoresponder',
    name: 'Mautic',
    icon: '📊',
    descKey: 'integrations.mautic.desc',
    defaultDesc: 'Open-source marketing automation platform.',
    secretFields: ['password', 'apiToken'],
    fields: [
      { key: 'baseUrl', labelKey: 'integrations.field.baseUrl', defaultLabel: 'Mautic Base URL', type: 'text', required: true, placeholder: 'https://mautic.example.com' },
      { key: 'apiToken', labelKey: 'integrations.field.bearerToken', defaultLabel: 'Bearer Token (or Username/Password below)', type: 'password' },
      { key: 'username', labelKey: 'integrations.field.username', defaultLabel: 'Basic Auth Username (Optional)', type: 'text' },
      { key: 'password', labelKey: 'integrations.field.password', defaultLabel: 'Basic Auth Password (Optional)', type: 'password' },
    ],
  },
  mailwizz: {
    provider: 'mailwizz',
    kind: 'autoresponder',
    name: 'MailWizz',
    icon: '📬',
    descKey: 'integrations.mailwizz.desc',
    defaultDesc: 'Self-hosted email marketing software API.',
    secretFields: ['secretKey'],
    fields: [
      { key: 'apiUrl', labelKey: 'integrations.field.apiUrl', defaultLabel: 'MailWizz API URL', type: 'text', required: true, placeholder: 'https://newsletter.example.com/api' },
      { key: 'publicKey', labelKey: 'integrations.field.publicKey', defaultLabel: 'Public Key', type: 'text', required: true },
      { key: 'secretKey', labelKey: 'integrations.field.secretKey', defaultLabel: 'Secret Key', type: 'password', required: true },
      { key: 'listUid', labelKey: 'integrations.field.listUid', defaultLabel: 'List Unique ID', type: 'text', required: true, placeholder: 'xx123yy456' },
    ],
  },
};

// ── Helpers ──────────────────────────────────────────────────────────────────

/**
 * Ensures that password/secret fields that are masked ('••••••••') or unchanged
 * transmit '••••••••' so that the backend preserves the existing ciphertext.
 * Also coerces number fields defined in the schema.
 */
export function prepareProfileConfigForSave(
  provider: ProviderName,
  formConfig: Record<string, any>,
  originalConfig?: Record<string, any>
): Record<string, any> {
  const meta = PROVIDER_REGISTRY[provider];
  const secretKeys = new Set(meta?.secretFields || []);
  const clean: Record<string, any> = { ...formConfig };

  // Convert numbers for number fields
  for (const f of meta?.fields || []) {
    if (f.type === 'number' && clean[f.key] !== undefined && clean[f.key] !== '') {
      clean[f.key] = Number(clean[f.key]);
    }
  }

  for (const key of secretKeys) {
    const val = clean[key];
    if (val === '••••••••' || (!val && originalConfig?.[key] === '••••••••')) {
      clean[key] = '••••••••';
    }
  }
  return clean;
}

// ── SMS Segment Counter (GSM-7 vs Unicode) ───────────────────────────────────

export interface SmsSegmentCalculation {
  charCount: number;
  segmentCount: number;
  encoding: 'GSM-7' | 'Unicode';
  remainingInSegment: number;
  maxPerSegment: number;
}

/** Standard GSM 03.38 7-bit character set validation */
export const GSM_7_REGEX =
  /^[@£$¥èéùìòÇ\nØø\rÅåΔ_ΦΓΛΩΠΨΣΘΞÆæßÉ !"#¤%&'()*+,\-./0-9:;<=>?¡A-ZÄÖÑÜ§¿a-zäöñüà\f^{}\\[~\]|€]*$/;

export function calculateSmsSegments(text: string): SmsSegmentCalculation {
  const charCount = text.length;
  if (charCount === 0) {
    return {
      charCount: 0,
      segmentCount: 0,
      encoding: 'GSM-7',
      remainingInSegment: 160,
      maxPerSegment: 160,
    };
  }

  const isGsm = GSM_7_REGEX.test(text);
  const encoding: 'GSM-7' | 'Unicode' = isGsm ? 'GSM-7' : 'Unicode';
  const singleLimit = isGsm ? 160 : 70;
  const multiLimit = isGsm ? 153 : 67;

  if (charCount <= singleLimit) {
    return {
      charCount,
      segmentCount: 1,
      encoding,
      remainingInSegment: singleLimit - charCount,
      maxPerSegment: singleLimit,
    };
  }

  const segmentCount = Math.ceil(charCount / multiLimit);
  const remainingInSegment = segmentCount * multiLimit - charCount;
  return {
    charCount,
    segmentCount,
    encoding,
    remainingInSegment,
    maxPerSegment: multiLimit,
  };
}

// ── Callables ────────────────────────────────────────────────────────────────

export const integrationProfileSave = call<
  {
    id?: string;
    kind: IntegrationKind;
    provider: string;
    name: string;
    config: Record<string, any>;
  },
  { ok: boolean; id: string }
>('integrationProfileSave');

export const integrationProfileDelete = call<
  { id: string },
  { ok: boolean }
>('integrationProfileDelete');

export const integrationProfileList = call<
  { kind?: IntegrationKind },
  { ok: boolean; profiles: IntegrationProfileDoc[] }
>('integrationProfileList');

export const integrationProfileTest = call<
  { id: string; testRecipient?: string },
  { ok: boolean; verified: boolean; lastError?: string | null }
>('integrationProfileTest');
